import styled from 'styled-components';
import {useState, memo} from 'react';
import {Box} from '@sproutsocial/seeds-react-box';
import {Icon} from '@sproutsocial/seeds-react-icon';

import {type EnumMediaType, type TypePageData} from '../services';

import {getPostImageAriaLabel} from '../utils';
import {pollinateSproutLink} from '../pollination';

const MEDIA_TYPES = {
	video: 'video',
	carousel: 'carousel',
} as const;

const MediaIconContainer = styled(Box)`
	position: absolute;
	top: ${props => props.theme.space[300]};
	right: ${props => props.theme.space[300]};
	padding: ${props => props.theme.space[200]};
	color: ${props => props.theme.colors.neutral[0]};
	font-size: 0;
`;

/**
 * @example
 *   // For 4:5 aspect ratio
 *   <PostLink href='example.com' aspectRatio={4 / 5}>Link content here!</PostLink>
 */
const PostLink = styled.a<{aspectRatio: number}>`
	display: block;
	position: relative;
	overflow: hidden;

	/* Aspect ratio padding trick */
	padding-bottom: ${({aspectRatio}) => 100 / aspectRatio}%;

	&:hover,
	&:focus {
		opacity: 0.7;
	}
`;

const PostImage = styled.img`
	// Stretch image within 1:1 container
	position: absolute;
	top: 0;
	left: 0;
	height: 100%;
	width: 100%;
	max-width: 100%;

	// Crop image to conform to parent 1:1 aspect ratio
	object-fit: cover;
`;

type TypeProps = {
	link: string;
	mediaURL: string;
	mediaType: EnumMediaType;
	page: TypePageData | null | undefined;
	postDate: number;
	profileName: string;
	postGuid: string;
};

const Post = ({link, mediaURL, mediaType, page, postDate, profileName, postGuid}: TypeProps) => {
	const [image_exists, setImageExists] = useState(true);

	const pollinatePostClick = () => {
		pollinateSproutLink({feature: 'instagram_post', action: postGuid, id: page?.page_id}, {destination_url: link});
	};

	const ariaLabel = getPostImageAriaLabel(profileName, postDate);

	if (!image_exists) {
		return null;
	}

	return (
		<PostLink aspectRatio={3 / 4} href={link} target='_blank' rel='noreferrer' onClick={pollinatePostClick}>
			<PostImage
				src={mediaURL}
				alt={ariaLabel}
				onError={() => {
					setImageExists(false);
				}}
			/>
			{mediaType === MEDIA_TYPES.video && (
				<MediaIconContainer>
					<Icon name='video-camera-solid' ariaLabel='Video Media Type' color='neutral.0' />
				</MediaIconContainer>
			)}
			{mediaType === MEDIA_TYPES.carousel && (
				<MediaIconContainer className='postContainer-mediaType'>
					<Icon name='images-solid' ariaLabel='Carousel Media Type' color='neutral.0' />
				</MediaIconContainer>
			)}
		</PostLink>
	);
};

export default memo<TypeProps>(Post);
